/**
 * CV Page Component - Waveform Timeline
 * =====================================
 *
 * Horizontal waveform-style timeline where events are positioned by actual dates
 * and stacked vertically to create density peaks/valleys.
 * Features image preview on hover.
 */

import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import MetaTags, { getLegalPageStructuredData } from '../ui/MetaTags';
import './CVPage.scss';

const CVPage = ({ data }) => {
  const events = data?.events || [];
  const pressItems = data?.press_mentions || [];
  const awards = data?.awards || [];
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoveredImageUrl, setHoveredImageUrl] = useState(null); // Track specific image being hovered
  const [isVideoPoster, setIsVideoPoster] = useState(false); // Track if hovered image is a video poster
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 }); // Position when hover starts
  const mousePositionRef = useRef({ x: 0, y: 0 }); // Ref for thumbnail position (used by useLayoutEffect)
  const tooltipRef = useRef(null); // Ref for tooltip element to measure height
  const tooltipHeightRef = useRef(0); // Cache tooltip height to avoid re-measuring
  const tooltipWidthRef = useRef(0); // Cache tooltip width to avoid re-measuring
  const thumbnailPositionRef = useRef({ x: 0, y: 0, width: 0 }); // Store thumbnail position and width for stable tooltip positioning
  const TOOLTIP_MAX_WIDTH = 400; // CSS max-width value - use this for positioning calculations
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1600
  );
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 900
  );

  // Update viewport dimensions on resize
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Measure tooltip height and width when it first appears and apply positioning
  useLayoutEffect(() => {
    if (hoveredItem && tooltipRef.current) {
      // Always re-measure dimensions to ensure accuracy (content may change)
      const measuredHeight = tooltipRef.current.offsetHeight;
      const measuredWidth = tooltipRef.current.offsetWidth;

      // Cache for logging, but use fresh measurements for positioning
      tooltipHeightRef.current = measuredHeight;
      tooltipWidthRef.current = measuredWidth;

      const thumbnailPos = thumbnailPositionRef.current;

      // console.log('[Tooltip] Dimensions:', { width: measuredWidth, height: measuredHeight });

      // Calculate desired position based on thumbnail center
      // Vertical: align tooltip bottom with thumbnail center (top = thumbnailY - tooltipHeight)
      const desiredTop = thumbnailPos.y - measuredHeight;

      // Horizontal: position tooltip to avoid overlapping the thumbnail column
      // For left quadrants: move 50% of thumbnail width to the right + 8px gap
      // For right quadrants: move 50% of thumbnail width to the left + 8px gap
      const isRightHalf = thumbnailPos.x > viewportWidth / 2;
      const thumbnailWidth = thumbnailPos.width || 48; // Default to 48px if not available
      const columnOffset = thumbnailWidth / 2; // 50% of column width
      const gap = 8; // 8px gap

      const desiredLeft = isRightHalf
        ? thumbnailPos.x - measuredWidth - columnOffset - gap // Position to the left, offset by half column + gap
        : thumbnailPos.x + columnOffset + gap; // Position to the right, offset by half column + gap

      // console.log('[Tooltip] Calculated:', {
      //   top: desiredTop,
      //   left: desiredLeft,
      //   thumbnailWidth,
      //   columnOffset,
      //   gap,
      //   calculation: isRightHalf
      //     ? `${thumbnailPos.x} - ${measuredWidth} - ${columnOffset} - ${gap} = ${desiredLeft}`
      //     : `${thumbnailPos.x} + ${columnOffset} + ${gap} = ${desiredLeft}`
      // });

      // Apply viewport constraints
      let adjustedTop = desiredTop;
      let adjustedLeft = desiredLeft;
      const adjustments = [];

      // Vertical constraints
      if (adjustedTop < 0) {
        adjustments.push('clamped top to 0');
        adjustedTop = 0;
      }
      if (adjustedTop + measuredHeight > viewportHeight) {
        adjustments.push(`clamped bottom to viewport (${viewportHeight})`);
        adjustedTop = viewportHeight - measuredHeight;
      }

      // Horizontal constraints
      if (adjustedLeft < 0) {
        adjustments.push('clamped left to 0');
        adjustedLeft = 0;
      }
      if (adjustedLeft + measuredWidth > viewportWidth) {
        adjustments.push(`clamped right to viewport (${viewportWidth})`);
        adjustedLeft = viewportWidth - measuredWidth;
      }

      const reason = adjustments.length > 0 ? adjustments.join(', ') : 'no adjustment needed';
      // console.log('[Tooltip] Adjusted:', { top: adjustedTop, left: adjustedLeft, reason });

      // Apply positioning directly via DOM
      tooltipRef.current.style.left = `${adjustedLeft}px`;
      tooltipRef.current.style.top = `${adjustedTop}px`;
      tooltipRef.current.style.transform = 'none'; // Remove transform

      // Update state for React (for initial render)
      setHoverPosition({ x: adjustedLeft, y: adjustedTop });
    } else if (!hoveredItem) {
      // Reset caches when tooltip disappears
      tooltipHeightRef.current = 0;
      tooltipWidthRef.current = 0;
    }
  }, [hoveredItem, viewportHeight, viewportWidth]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Check if an image URL is a video poster
  const isVideoPosterImage = (item, imageUrl) => {
    if (!item || !imageUrl || !item.preview_media) return false;

    // Extract the relative path from the full URL
    const relativePath = imageUrl.replace(/^\/assets\/images\//, '');

    // Check if any video in preview_media has this as its poster
    return item.preview_media.some(media => {
      if (media.type === 'video' && media.poster) {
        const posterPath = media.poster.replace(/^\/?assets\/images\//, '');
        return posterPath === relativePath;
      }
      return false;
    });
  };

  // Handle mouse enter with item data and specific image URL
  const handleMouseEnter = (item, e, imageUrl = null) => {
    // Use provided imageUrl, or fall back to getItemImage
    const image = imageUrl || getItemImage(item);
    if (image) {
      // Check if this is a video poster
      const isPoster = isVideoPosterImage(item, image);
      setIsVideoPoster(isPoster);

      // Get thumbnail element position for stable tooltip positioning
      const thumbnailRect = e.currentTarget.getBoundingClientRect();
      // Use thumbnail center position (more stable than cursor position)
      const thumbnailPos = {
        x: thumbnailRect.left + thumbnailRect.width / 2,
        y: thumbnailRect.top + thumbnailRect.height / 2,
        width: thumbnailRect.width
      };

      // Determine quadrant
      const isRightHalf = thumbnailPos.x > viewportWidth / 2;
      const isBottomHalf = thumbnailPos.y > viewportHeight / 2;
      const quadrant = {
        horizontal: isRightHalf ? 'right' : 'left',
        vertical: isBottomHalf ? 'bottom' : 'top'
      };

      // console.log('[Tooltip] Thumbnail:', item.title || item.id);
      // console.log('[Tooltip] Position:', { x: thumbnailPos.x, y: thumbnailPos.y });
      // console.log('[Tooltip] Quadrant:', quadrant);
      // console.log('[Tooltip] Viewport:', { width: viewportWidth, height: viewportHeight });

      // Store thumbnail position for tooltip positioning
      thumbnailPositionRef.current = thumbnailPos;
      mousePositionRef.current = thumbnailPos; // Used by useLayoutEffect

      // Also update state for re-renders
      setHoverPosition(thumbnailPos);

      // Set hovered item and specific image
      setHoveredItem(item);
      setHoveredImageUrl(image);
    } else {
      setIsVideoPoster(false);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setHoveredItem(null);
    setHoveredImageUrl(null);
    setIsVideoPoster(false);
    setHoverPosition({ x: 0, y: 0 });
    thumbnailPositionRef.current = { x: 0, y: 0 };
  };


  // Get first image from item
  const getItemImage = (item) => {
    // For events with preview_images array
    if (item.preview_images && item.preview_images.length > 0) {
      return `/assets/images/${item.preview_images[0].jpeg || item.preview_images[0].path}`;
    }
    // For events with preview_media array
    if (item.preview_media && item.preview_media.length > 0) {
      // First try to find an image (type: 'image' OR has jpeg/path but not a video)
      const firstImage = item.preview_media.find(media => {
        if (media.type === 'image') return true;
        if (media.type === 'video') return false;
        const imagePath = media.jpeg || media.path;
        return imagePath && !imagePath.match(/\.(mp4|mov|m4v|webm|ogg)$/i);
      });
      if (firstImage) {
        return `/assets/images/${firstImage.jpeg || firstImage.path}`;
      }
      // Fallback to video poster
      const firstVideo = item.preview_media.find(media => media.type === 'video');
      if (firstVideo && firstVideo.poster) {
        return `/assets/images/${firstVideo.poster}`;
      }
    }
    // For press with preview_image object
    if (item.preview_image && item.preview_image.jpeg) {
      return `/assets/images/${item.preview_image.jpeg}`;
    }
    return null;
  };

  // Count actual thumbnails that will be rendered for an event
  const countEventThumbnails = (event) => {
    const eventImages = [];
    const seenImages = new Set();

    // Add images from preview_images array
    if (event.preview_images && event.preview_images.length > 0) {
      event.preview_images.forEach(img => {
        const imagePath = img.jpeg || img.path;
        if (imagePath) {
          const fullPath = `/assets/images/${imagePath}`;
          if (!seenImages.has(fullPath)) {
            eventImages.push(fullPath);
            seenImages.add(fullPath);
          }
        }
      });
    }

    // Add images from preview_media array
    if (event.preview_media && event.preview_media.length > 0) {
      event.preview_media.forEach(media => {
        if (media.type === 'video' && media.poster) {
          const fullPath = `/assets/images/${media.poster}`;
          if (!seenImages.has(fullPath)) {
            eventImages.push(fullPath);
            seenImages.add(fullPath);
          }
        } else if (media.type === 'image' || (!media.type || media.type !== 'video')) {
          const imagePath = media.jpeg || media.path;
          if (imagePath && !imagePath.match(/\.(mp4|mov|m4v|webm|ogg)$/i)) {
            const fullPath = `/assets/images/${imagePath}`;
            if (!seenImages.has(fullPath)) {
              eventImages.push(fullPath);
              seenImages.add(fullPath);
            }
          }
        }
      });
    }

    // If no images, return 1 for placeholder
    return eventImages.length > 0 ? eventImages.length : 1;
  };

  // Calculate timeline scale and group events into time buckets
  const timelineData = useMemo(() => {
    // Collect all items with dates
    const allItems = [];

    // Add events (only those with images)
    events.forEach(event => {
      if (event.date && event.date.trim()) {
        const hasImage = getItemImage(event) !== null;
        if (!hasImage) return; // Skip events without images

        // Find related press (only those with images)
        const relatedPress = event.press_refs
          ? pressItems.filter(press => {
            const pressHasImage = press.preview_image && press.preview_image.jpeg;
            return event.press_refs.includes(press.id) && pressHasImage;
            })
          : [];

        allItems.push({
          ...event,
          type: 'event',
          date: event.date,
          relatedPress: relatedPress
        });
      }
    });

    // Add standalone press (not referenced by events)
    const referencedPressIds = new Set();
    events.forEach(event => {
      if (event.press_refs) {
        event.press_refs.forEach(id => referencedPressIds.add(id));
      }
    });

    // Add standalone press (not referenced by events, only those with images)
    pressItems.forEach(press => {
      const hasImage = press.preview_image && press.preview_image.jpeg;
      if (!referencedPressIds.has(press.id) && press.date && hasImage) {
        allItems.push({
          ...press,
          type: 'press',
          date: press.date
        });
      }
    });

    // Add awards
    awards.forEach(award => {
      if (award.year) {
        allItems.push({
          ...award,
          type: 'award',
          date: `${award.year}-01-01`
        });
      }
    });

    if (allItems.length === 0) {
      return { timeBuckets: [], yearMarkers: [], startDate: null, endDate: null };
    }

    // Calculate date range
    const dates = allItems.map(item => new Date(item.date)).filter(d => !isNaN(d.getTime()));
    const startDate = new Date(Math.min(...dates));
    const endDate = new Date(Math.max(...dates));

    // Add padding (1 month before/after)
    startDate.setMonth(startDate.getMonth() - 1);
    endDate.setMonth(endDate.getMonth() + 1);

    // Calculate timeline width to fit viewport (desktop: ~90% of 1920px = ~1728px, with padding)
    // Use viewport width minus padding, or minimum 1400px for very wide screens
    const timelineWidth = Math.max(1400, Math.min(viewportWidth - 200, 1800)); // Fit on desktop

    // Calculate number of months between start and end
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth();
    const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1; // +1 to include both start and end months

    // Determine best block size: monthly blocks for good granularity
    const blockSize = 'month'; // Use monthly blocks
    const totalBlocks = totalMonths;

    // Spacing: vertical gap between thumbnails, horizontal gap between columns (must match)
    const thumbnailGap = 3; // Reduced vertical gap
    const maxGapWidth = 20; // Maximum gap width between content columns (even if many empty months between)

    // Thumbnail size - will be calculated after we know how many content columns we have
    const minThumbnailSize = 48;

    // Group items into monthly time buckets (equal-width grid)
    const timeBuckets = {};

    allItems.forEach(item => {
      const itemDate = new Date(item.date);
      const itemYear = itemDate.getFullYear();
      const itemMonth = itemDate.getMonth();

      // Calculate which month block this item belongs to
      const blockIndex = (itemYear - startYear) * 12 + (itemMonth - startMonth);

      // Ensure blockIndex is within bounds
      const bucketKey = Math.max(0, Math.min(blockIndex, totalBlocks - 1));

      if (!timeBuckets[bucketKey]) {
        timeBuckets[bucketKey] = {
          key: bucketKey,
          year: itemYear,
          month: itemMonth,
          events: [],
          press: [],
          awards: []
        };
      }

      if (item.type === 'event') {
        timeBuckets[bucketKey].events.push(item);
        // Add related press to the same bucket or nearby bucket
        if (item.relatedPress && item.relatedPress.length > 0) {
          item.relatedPress.forEach(press => {
            const pressDate = new Date(press.date);
            const pressYear = pressDate.getFullYear();
            const pressMonth = pressDate.getMonth();
            const pressBlockIndex = (pressYear - startYear) * 12 + (pressMonth - startMonth);
            const pressBucketKey = Math.max(0, Math.min(pressBlockIndex, totalBlocks - 1));

            if (!timeBuckets[pressBucketKey]) {
              timeBuckets[pressBucketKey] = {
                key: pressBucketKey,
                year: pressYear,
                month: pressMonth,
                events: [],
                press: [],
                awards: []
              };
            }

            if (!timeBuckets[pressBucketKey].press.find(p => p.id === press.id)) {
              timeBuckets[pressBucketKey].press.push(press);
            }
          });
        }
      } else if (item.type === 'press') {
        if (!timeBuckets[bucketKey].press.find(p => p.id === item.id)) {
          timeBuckets[bucketKey].press.push(item);
        }
      } else if (item.type === 'award') {
        timeBuckets[bucketKey].awards.push(item);
      }
    });

    // Filter to only buckets with content and calculate heights
    // Reverse sort: newest dates first (left side), oldest dates last (right side)
    const allBuckets = Object.values(timeBuckets);
    const contentBuckets = allBuckets.filter(bucket => {
      const eventCount = bucket.events.length;
      const pressCount = bucket.press.length;
      const awardCount = bucket.awards.length;
      return (eventCount + pressCount + awardCount) > 0;
    }).sort((a, b) => b.key - a.key); // Reverse: newest first

    // Calculate column width based on content columns only
    // Available width = timelineWidth - (gaps between columns)
    // Gap count = contentBuckets.length - 1
    const gapCount = Math.max(0, contentBuckets.length - 1);
    const totalGapWidth = gapCount * maxGapWidth;
    const availableWidth = timelineWidth - totalGapWidth;

    // Calculate thumbnail size first (based on available space)
    // Ensure minimum thumbnail size, then calculate column width to fit
    const minColumnWidth = minThumbnailSize + 2; // Thumbnail + 2px margin
    const calculatedColumnWidth = contentBuckets.length > 0 ? availableWidth / contentBuckets.length : minColumnWidth;

    // Use the larger of calculated width or minimum required width
    const columnWidth = Math.max(minColumnWidth, calculatedColumnWidth);

    // Calculate thumbnail size based on column width (with margin)
    const thumbnailSize = Math.min(minThumbnailSize, Math.max(30, columnWidth - 2)); // Min 30px, max 48px, leave 2px margin
    const thumbnailHeight = thumbnailSize;

    // Calculate items per column based on available viewport height
    // Match the CSS calculation chain:
    // cv-content height = calc(100vh - var(--space-3xl) * 2) = viewportHeight - 128px (64px * 2)
    // waveform-timeline height = calc(100% - 80px) = cv-content height - 80px
    // waveform-timeline has padding: var(--space-lg) top (24px) + var(--space-sm) bottom (8px) = 32px total
    // timeline-waveform height = calc(100% - 30px) where 100% = waveform-timeline content area
    // Actual: timeline-waveform height = waveform-timeline height - padding (32px) - axis space (30px) = waveform-timeline height - 62px
    const cvContentHeight = viewportHeight - 128; // Subtract top/bottom padding (var(--space-3xl) * 2 = 64px * 2)
    const waveformTimelineHeight = cvContentHeight - 80; // Subtract header height (80px)
    const waveformTimelinePadding = 24 + 8; // var(--space-lg) top (24px) + var(--space-sm) bottom (8px)
    const axisSpace = 30; // Space for timeline axis
    const timelineWaveformHeight = waveformTimelineHeight - waveformTimelinePadding - axisSpace; // Subtract padding and axis
    const availableHeight = Math.max(400, timelineWaveformHeight); // Minimum 400px
    const itemsPerColumn = Math.max(1, Math.floor(availableHeight / (thumbnailHeight + thumbnailGap)));

    // Split buckets with too many items into multiple columns
    const expandedBuckets = [];
    let currentPosition = 0;

    contentBuckets.forEach((bucket, index) => {
      // Count actual thumbnails that will be rendered (events can have multiple images)
      // Build array of items with their thumbnail counts
      const itemsWithCounts = [];

      bucket.events.forEach(event => {
        const thumbnailCount = countEventThumbnails(event);
        itemsWithCounts.push({ item: event, type: 'event', thumbnailCount });
      });

      bucket.press.forEach(press => {
        itemsWithCounts.push({ item: press, type: 'press', thumbnailCount: 1 });
      });

      bucket.awards.forEach(award => {
        itemsWithCounts.push({ item: award, type: 'award', thumbnailCount: 1 });
      });

      // Calculate total thumbnails
      const totalThumbnails = itemsWithCounts.reduce((sum, item) => sum + item.thumbnailCount, 0);

      // If bucket has more thumbnails than fit in one column, split into multiple columns
      // Check based on calculated height, not just thumbnail count
      const maxHeightForBucket = (totalThumbnails * thumbnailHeight) + ((totalThumbnails - 1) * thumbnailGap);
      const needsSplitting = maxHeightForBucket > timelineWaveformHeight;

      if (needsSplitting) {
        // Add gap before this bucket (except for first bucket)
        if (index > 0) {
          currentPosition += maxGapWidth;
        }

        // Distribute items across columns, keeping complete items together
        const subColumns = [];
        let currentColumn = { events: [], press: [], awards: [], thumbnailCount: 0 };

        itemsWithCounts.forEach(itemWithCount => {
          // Calculate what the height would be if we add this item
          const newThumbnailCount = currentColumn.thumbnailCount + itemWithCount.thumbnailCount;
          const newCalculatedHeight = (newThumbnailCount * thumbnailHeight) + ((newThumbnailCount - 1) * thumbnailGap);

          // If adding this item would exceed the height limit, start a new column
          // Always check, even if current column is empty (in case a single item is too large)
          if (newCalculatedHeight > timelineWaveformHeight) {
            // If current column has items, save it before starting new one
            if (currentColumn.thumbnailCount > 0) {
              subColumns.push(currentColumn);
            }
            // Start a new column for this item
            currentColumn = { events: [], press: [], awards: [], thumbnailCount: 0 };

            // Check if this single item itself exceeds the limit
            const singleItemHeight = (itemWithCount.thumbnailCount * thumbnailHeight) + ((itemWithCount.thumbnailCount - 1) * thumbnailGap);
            if (singleItemHeight > timelineWaveformHeight) {
              console.warn(`[CV Timeline] Single item exceeds max height! Item has ${itemWithCount.thumbnailCount} thumbnails, height would be ${singleItemHeight}px, max is ${timelineWaveformHeight}px. Item will still be added but column will be clamped by CSS.`);
              // Even though it exceeds, we still need to add it - CSS max-height will clamp it
              // But we should still add it to prevent data loss
            }
          }

          // Add item to current column
          // Note: If item exceeds limit, CSS max-height will clamp the visual height
          if (itemWithCount.type === 'event') {
            currentColumn.events.push(itemWithCount.item);
          } else if (itemWithCount.type === 'press') {
            currentColumn.press.push(itemWithCount.item);
          } else if (itemWithCount.type === 'award') {
            currentColumn.awards.push(itemWithCount.item);
          }
          currentColumn.thumbnailCount += itemWithCount.thumbnailCount;
        });

        // Add the last column if it has items
        if (currentColumn.thumbnailCount > 0) {
          subColumns.push(currentColumn);
        }

        // Create sub-buckets for each column
        subColumns.forEach((subColumn, colIndex) => {
          const calculatedHeight = (subColumn.thumbnailCount * thumbnailHeight) + ((subColumn.thumbnailCount - 1) * thumbnailGap);
          const maxAllowedHeight = timelineWaveformHeight;

          // Validate height constraint
          if (calculatedHeight > maxAllowedHeight) {
            console.warn(`[CV Timeline] Column ${bucket.key}-${colIndex} height (${calculatedHeight}px) exceeds max (${maxAllowedHeight}px)!`, {
              thumbnailCount: subColumn.thumbnailCount,
              calculatedHeight,
              maxAllowedHeight,
              thumbnailHeight,
              thumbnailGap
            });
          }

          const subBucket = {
            ...bucket,
            key: `${bucket.key}-${colIndex}`, // Unique key for each sub-column
            events: subColumn.events,
            press: subColumn.press,
            awards: subColumn.awards,
            totalItems: subColumn.thumbnailCount,
            totalHeight: calculatedHeight,
            columnWidth: columnWidth,
            position: currentPosition + (colIndex * columnWidth),
            isSubColumn: true,
            originalBucketKey: bucket.key
          };

          expandedBuckets.push(subBucket);
        });

        // Update currentPosition for next bucket (account for all sub-columns)
        currentPosition += columnWidth * subColumns.length;
      } else {
        // Single column bucket
        if (index > 0) {
          currentPosition += maxGapWidth;
        }

        const calculatedHeight = (totalThumbnails * thumbnailHeight) + ((totalThumbnails - 1) * thumbnailGap);
        const maxAllowedHeight = timelineWaveformHeight;

        // Validate height constraint
        if (calculatedHeight > maxAllowedHeight) {
          console.warn(`[CV Timeline] Column ${bucket.key} height (${calculatedHeight}px) exceeds max (${maxAllowedHeight}px)!`, {
            thumbnailCount: totalThumbnails,
            calculatedHeight,
            maxAllowedHeight,
            thumbnailHeight,
            thumbnailGap
          });
        }

        bucket.position = currentPosition;
        bucket.totalItems = totalThumbnails;
        bucket.totalHeight = calculatedHeight;
        bucket.columnWidth = columnWidth;
        bucket.isSubColumn = false;

        expandedBuckets.push(bucket);
        currentPosition += columnWidth;
      }
    });

    // Generate year markers based on actual content positions
    // Since buckets are reversed (newest first), iterate years in reverse too
    const yearMarkers = [];
    const currentYear = startDate.getFullYear();
    const markerEndYear = endDate.getFullYear();

    // Iterate years in reverse (newest first, matching reversed buckets)
    for (let year = markerEndYear; year >= currentYear; year--) {
      const yearDate = new Date(`${year}-01-01`);
      if (yearDate >= startDate && yearDate <= endDate) {
        const yearBlockIndex = (year - startYear) * 12 + (0 - startMonth); // January = month 0

        // Find the bucket matching this year (use original buckets before splitting)
        // Find first sub-column or single column for this year
        const yearBucket = expandedBuckets.find(b => {
          const bucketKey = typeof b.key === 'string' ? parseInt(b.key.split('-')[0]) : b.key;
          return bucketKey === yearBlockIndex;
        }) || expandedBuckets.find(b => {
          const bucketKey = typeof b.key === 'string' ? parseInt(b.key.split('-')[0]) : b.key;
          return bucketKey <= yearBlockIndex;
        }) || expandedBuckets[expandedBuckets.length - 1];
        if (yearBucket) {
          yearMarkers.push({
            year,
            position: yearBucket.position
          });
        }
      }
    }

    // Calculate actual timeline width based on content
    // This may be wider than the original timelineWidth if columns need more space
    const actualTimelineWidth = expandedBuckets.length > 0
      ? currentPosition
      : timelineWidth;

    // Ensure timeline is at least as wide as the viewport allows
    const finalTimelineWidth = Math.max(actualTimelineWidth, timelineWidth);

    return {
      timeBuckets: expandedBuckets, // Expanded buckets (may include split columns)
      yearMarkers,
      startDate,
      endDate,
      timelineWidth: finalTimelineWidth,
      columnWidth,
      totalBlocks: expandedBuckets.length, // Total columns (including split ones)
      blockSize,
      thumbnailSize,
      maxGapWidth,
      itemsPerColumn,
      timelineWaveformHeight
    };
  }, [events, pressItems, awards, viewportWidth, viewportHeight]);

  return (
    <>
      <MetaTags
        title="CV / Timeline - Elodie Carstensen"
        description="Complete chronological timeline of events, exhibitions, press coverage, and awards."
        url="https://www.elodiecarstensen.com/about/cv"
        structuredData={getLegalPageStructuredData(
          'CV / Timeline - Elodie Carstensen',
          'https://www.elodiecarstensen.com/about/cv'
        )}
      />
      <div className="cv-page">
        <div className="cv-content">
          <div className="cv-header">
            <h1>TIMELINE OVERVIEW</h1>
          </div>

          {/* Waveform Timeline */}
          <div className="waveform-timeline">
            {/* Timeline Container */}
            <div
              className="timeline-waveform-container"
              style={{ width: `${timelineData.timelineWidth}px` }}
            >
              {/* Timeline Arrow Axis */}
              <div className="timeline-axis-arrow" />

              {/* Waveform Columns - Only render content columns */}
              <div className="timeline-waveform">
                {timelineData.timeBuckets.map((bucket) => {
                  return (
                    <div
                      key={bucket.key}
                      className="waveform-column"
                      style={{
                        left: `${bucket.position}px`,
                        width: `${bucket.columnWidth}px`,
                        maxWidth: `${bucket.columnWidth}px`,
                        height: `${bucket.totalHeight}px`,
                        maxHeight: `${timelineData.timelineWaveformHeight || '729px'}px`,
                        margin: 0,
                        padding: 0,
                        boxSizing: 'border-box'
                      }}
                    >
                      {/* All items stacked to top */}
                      {(bucket.events.length > 0 || bucket.press.length > 0 || bucket.awards.length > 0) && (
                        <div className="waveform-column-top">
                          {/* Events - render all images */}
                          {bucket.events.map(event => {
                            const eventIndex = events.findIndex(e => e.id === event.id);
                            const timelineUrl = eventIndex !== -1 ? `/about?e=${eventIndex + 1}` : null;

                            // Get all images for this event (from preview_images and preview_media)
                            const eventImages = [];
                            const seenImages = new Set(); // Deduplicate images

                            // Add images from preview_images array
                            if (event.preview_images && event.preview_images.length > 0) {
                              event.preview_images.forEach(img => {
                                const imagePath = img.jpeg || img.path;
                                if (imagePath) {
                                  const fullPath = `/assets/images/${imagePath}`;
                                  if (!seenImages.has(fullPath)) {
                                    eventImages.push(fullPath);
                                    seenImages.add(fullPath);
                                  }
                                }
                              });
                            }

                            // Add images from preview_media array
                            if (event.preview_media && event.preview_media.length > 0) {
                              event.preview_media.forEach(media => {
                                // Check if it's a video with a poster
                                if (media.type === 'video' && media.poster) {
                                  const fullPath = `/assets/images/${media.poster}`;
                                  if (!seenImages.has(fullPath)) {
                                    eventImages.push(fullPath);
                                    seenImages.add(fullPath);
                                  }
                                }
                                // Check if it's an image (has type: 'image' OR has jpeg/path but no type or type is not 'video')
                                else if (media.type === 'image' || (!media.type || media.type !== 'video')) {
                                  const imagePath = media.jpeg || media.path;
                                  if (imagePath && !imagePath.match(/\.(mp4|mov|m4v|webm|ogg)$/i)) {
                                    const fullPath = `/assets/images/${imagePath}`;
                                    if (!seenImages.has(fullPath)) {
                                      eventImages.push(fullPath);
                                      seenImages.add(fullPath);
                                    }
                                  }
                                }
                              });
                            }

                            // If no images, show placeholder
                            if (eventImages.length === 0) {
                              return (
                                <Link
                                  key={event.id}
                                  to={timelineUrl || '#'}
                                  className="waveform-thumbnail clickable"
                                  style={{
                                    width: `${timelineData.thumbnailSize}px`,
                                    height: `${timelineData.thumbnailSize}px`
                                  }}
                                  onMouseEnter={(e) => handleMouseEnter(event, e)}
                                  onMouseLeave={handleMouseLeave}
                                  title={event.title}
                                >
                                  <div className="waveform-thumbnail-placeholder">
                                    <span className="waveform-thumbnail-initial">
                                      {event.title.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                </Link>
                              );
                            }

                            // Render all images for this event
                            return eventImages.map((imageUrl, imgIndex) => (
                              <Link
                                key={`${event.id}-${imgIndex}`}
                                to={timelineUrl || '#'}
                                className="waveform-thumbnail clickable"
                                style={{
                                  width: `${timelineData.thumbnailSize}px`,
                                  height: `${timelineData.thumbnailSize}px`
                                }}
                                onMouseEnter={(e) => handleMouseEnter(event, e, imageUrl)}
                                onMouseLeave={handleMouseLeave}
                                title={event.title}
                              >
                                <img src={imageUrl} alt={`${event.title} - Image ${imgIndex + 1}`} />
                              </Link>
                            ));
                          })}

                          {/* Press */}
                          {bucket.press.map(press => {
                            const imageUrl = getItemImage(press);
                            return (
                              <a
                                key={press.id}
                                href={press.links && press.links[0] ? press.links[0] : '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="waveform-thumbnail waveform-thumbnail-press clickable"
                                style={{
                                  width: `${timelineData.thumbnailSize}px`,
                                  height: `${timelineData.thumbnailSize}px`
                                }}
                                onMouseEnter={(e) => handleMouseEnter(press, e, imageUrl)}
                                onMouseLeave={handleMouseLeave}
                                title={press.title}
                              >
                                {imageUrl ? (
                                  <img src={imageUrl} alt={press.title} />
                                ) : (
                                  <div className="waveform-thumbnail-placeholder">
                                    <span className="waveform-thumbnail-initial">
                                      {press.title.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                              </a>
                            );
                          })}

                          {/* Awards */}
                          {bucket.awards.map(award => (
                            <div
                              key={award.id}
                              className="waveform-thumbnail waveform-thumbnail-award"
                              style={{
                                width: `${timelineData.thumbnailSize}px`,
                                height: `${timelineData.thumbnailSize}px`
                              }}
                              title={award.title}
                            >
                              <div className="waveform-thumbnail-placeholder">
                                <span className="waveform-thumbnail-initial">
                                  {award.title.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* No date labels - years shown at bottom, details on hover */}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Year Markers at Bottom */}
            <div className="timeline-markers-bottom">
              {timelineData.yearMarkers.map(marker => (
                <div
                  key={marker.year}
                  className="timeline-marker"
                  style={{ left: `${marker.position}px` }}
                >
                  {marker.year}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hover image preview card */}
        {hoveredItem && hoveredImageUrl && (
          <div
            ref={(el) => {
              if (tooltipRef.current !== el) {
                tooltipRef.current = el;
              }
            }}
            className="cv-image-preview"
            style={{
              left: `${hoverPosition.x}px`,
              top: `${hoverPosition.y}px`,
              // Position is set directly via DOM in useLayoutEffect for accurate positioning
            }}
          >
            <div className="cv-image-preview-image-wrapper">
              <img src={hoveredImageUrl} alt={hoveredItem.title || 'Preview'} />
              {isVideoPoster && (
                <div className="cv-image-preview-play-overlay">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="cv-image-preview-play-icon"
                  >
                    <circle cx="24" cy="24" r="24" fill="rgba(0, 0, 0, 0.6)" />
                    <path
                      d="M18 14L18 34L32 24L18 14Z"
                      fill="white"
                    />
                  </svg>
                </div>
              )}
            </div>
            <div className="cv-image-preview-meta">
              <div className="cv-image-preview-date">
                {hoveredItem.date ? formatDate(hoveredItem.date) : hoveredItem.year}
              </div>
              <div className="cv-image-preview-title">{hoveredItem.title}</div>
              {hoveredItem.subtype && (
                <div className="cv-image-preview-subtype">{hoveredItem.subtype}</div>
              )}
              {hoveredItem.location && (
                <div className="cv-image-preview-location">@ {hoveredItem.location}</div>
              )}
              {hoveredItem.source && (
                <div className="cv-image-preview-source">— {hoveredItem.source}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CVPage;

